import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const GoogleCallback = () => {
    const navigate = useNavigate();
    const { handleGoogleCallback } = useAuth();
    const [status, setStatus] = useState('processing'); // processing, success, error
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const processCallback = async () => {
            try {
                // Parse the hash fragment to get the access token
                // URL looks like: /auth/google/callback#access_token=xxx&token_type=Bearer&expires_in=3599&scope=email%20profile
                const hash = window.location.hash.substring(1); // Remove the # character
                const params = new URLSearchParams(hash);
                
                const accessToken = params.get('access_token');
                const error = params.get('error');
                const errorDescription = params.get('error_description');

                if (error) {
                    throw new Error(errorDescription || error || 'Google authentication failed');
                }

                if (!accessToken) {
                    throw new Error('No access token received from Google');
                }

                // Send the access token to our backend
                const success = await handleGoogleCallback(accessToken);

                if (success) {
                    setStatus('success');
                    // Redirect to dashboard after a brief moment
                    setTimeout(() => {
                        navigate('/dashboard', { replace: true });
                    }, 1000);
                } else {
                    throw new Error('Failed to authenticate with the server');
                }
            } catch (err) {
                console.error('Google callback error:', err);
                setStatus('error');
                setErrorMessage(err.message || 'Authentication failed');
                // Redirect to login after showing error
                setTimeout(() => {
                    navigate('/login', { replace: true });
                }, 3000);
            }
        };

        processCallback();
    }, [handleGoogleCallback, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 text-center">
                {/* Logo */}
                <img 
                    src="/logo512.png" 
                    alt="MediGuide AI" 
                    className="w-16 h-16 mx-auto mb-6"
                />
                
                {status === 'processing' && (
                    <>
                        <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                            Signing you in...
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400">
                            Please wait while we complete your Google sign-in.
                        </p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                            Welcome!
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400">
                            Sign-in successful. Redirecting to your dashboard...
                        </p>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                            Sign-in Failed
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            {errorMessage}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                            Redirecting to login page...
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default GoogleCallback;
